import React, { useState, useEffect } from 'react';
import { useAuth } from './hooks/useAuth';
import { FinancialReport as FinancialReportModel, Attachment } from './models/Content';
import { Timestamp } from 'firebase/firestore';
import { getAllPosts, addPost, updatePost, deletePost } from './data/contentDatabase';
import { getAllUsers } from './data/userDatabase';
import { formatDate, formatTime } from './utils/dateUtils';
import { getCurrentTimezone, getLocale } from './config/settings';

import Spinner from './components/Spinner';
import AttachmentsDisplay from './components/AttachmentsDisplay';
import FileAttachmentUpload from './components/FileAttachmentUpload';
import PostActions from './components/shared/PostActions';
import SendQuestionButton from './components/shared/SendQuestionButton';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import { Card } from './components/ui/card';
import { User as UserIcon, Calendar, PlusCircle, FileText } from 'lucide-react';

const defaultFormData = {
  title: '',
  content: '',
  category: 'report' as const,
};

const FinancialReports: React.FC = () => {
  const { user, isAdmin, isSuperAdmin } = useAuth();
  const [reports, setReports] = useState<FinancialReportModel[]>([]);
  const [users, setUsers] = useState<{ [id: string]: string }>({});
  const [isLoading, setIsLoading] = useState(true);
  const [showNewForm, setShowNewForm] = useState(false);
  const [formData, setFormData] = useState(defaultFormData);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Custom date formatting function that shows yyyy-mm-dd with timezone
  const formatDateTimeWithTimezone = (timestamp: Timestamp) => {
    const date = timestamp.toDate();
    const timezone = getCurrentTimezone();
    const formattedDate = formatDate(timestamp);
    const formattedTime = formatTime(date.toISOString());
    
    // Get timezone abbreviation
    const locale = getLocale();
    const timezoneName = Intl.DateTimeFormat(locale, {
      timeZone: timezone,
      timeZoneName: 'short'
    }).formatToParts(date).find(part => part.type === 'timeZoneName')?.value || timezone;
    
    return `${formattedDate} ${formattedTime} ${timezoneName}`;
  };

  // Load users for author name display
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const usersList = await getAllUsers();
        const usersMap: { [id: string]: string } = {};
        usersList.forEach(u => {
          usersMap[u.id] = u.fullName;
        });
        setUsers(usersMap);
      } catch (error) {
        console.error('Failed to load users:', error);
      }
    };
    loadUsers();
  }, []);

  // Load reports - filter for only 'report' category
  useEffect(() => {
    const loadReports = async () => {
      try {
        setIsLoading(true);
        const posts = await getAllPosts('report'); // Only get report posts
        setReports(posts as FinancialReportModel[]);
      } catch (error) {
        console.error('Failed to load reports:', error);
        setReports([]);
      } finally {
        setIsLoading(false);
      }
    };
    loadReports();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !formData.title || !formData.content) return;

    // Only ensure database entry starts with "report" for document identification,
    // but keep the user's original title for display
    const dbTitle = formData.title.toLowerCase().startsWith('report') 
      ? formData.title 
      : `report ${formData.title}`;

    const postData = {
      ...formData,
      title: dbTitle, // This is stored in database for identification
      attachments,
      category: 'report' as const,
      authorId: user.id,
    };
    
    try {
      if (editingId) {
        await updatePost({ ...postData, id: editingId });
        setReports(prev => prev.map(r => r.id === editingId ? { ...r, ...postData } : r));
      } else {
        const newReport = await addPost(postData);
        setReports(prev => [newReport, ...prev]);
      }
      handleFormReset();
    } catch (error) {
      console.error('Failed to save report:', error);
    }
  };

  const handleFormReset = () => {
    setFormData(defaultFormData);
    setAttachments([]);
    setEditingId(null);
    setShowNewForm(false);
  };

  const startEdit = (report: FinancialReportModel) => {
    setFormData({ 
      title: getDisplayTitle(report.title),
      content: report.content, 
      category: 'report' 
    });
    setAttachments(report.attachments || []);
    setEditingId(report.id || null);
    setShowNewForm(true);
    window.scrollTo(0, 0);
  };

  const handleDelete = async (reportId: string) => {
    if (!window.confirm('Are you sure you want to delete this report?')) return;
    
    try {
      const report = reports.find(r => r.id === reportId);
      if (report) {
        await deletePost(report);
        setReports(prev => prev.filter(r => r.id !== reportId));
      }
    } catch (error) {
      console.error('Failed to delete report:', error);
    }
  };

  const handleToggleVisibility = async (reportId: string) => {
    try {
      const report = reports.find(r => r.id === reportId);
      if (report) {
        const updatedReport = { ...report, hidden: !report.hidden };
        await updatePost(updatedReport);
        setReports(prev => prev.map(r => r.id === reportId ? updatedReport : r));
      }
    } catch (error) {
      console.error('Failed to toggle visibility:', error);
    }
  };

  // Function to display title without "report" prefix
  const getDisplayTitle = (title: string) => {
    // Remove "report:" or "report " prefix for display
    return title.replace(/^report:?\s*/i, '');
  };

  if (isLoading && reports.length === 0) {
    return <Spinner fullScreen={true} className="bg-slate-50" />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Financial Reports</h1>
              <p className="text-slate-600 mt-2">Financial reports and documentation</p>
            </div>
            {isAdmin && (
              <Button 
                onClick={() => setShowNewForm(!showNewForm)}
                variant="outline"
                className="border-slate-300 text-slate-800 hover:bg-slate-50 hover:text-slate-900 h-auto px-4 py-1 text-sm font-medium rounded-lg transition-colors duration-200"
              >
                <PlusCircle className="w-4 h-4 mr-2" />
                {showNewForm ? 'Cancel' : 'Add New Report'}
              </Button>
            )}
          </div>
        </div>

      {showNewForm && (
        <Card className="p-6 mb-8 shadow-lg rounded-xl border border-slate-200">
          <form onSubmit={handleSubmit}>
            <div className="flex items-center mb-6">
              <FileText className="w-6 h-6 text-slate-600 mr-3" />
              <h2 className="text-xl font-semibold text-slate-900">{editingId ? 'Edit Report' : 'New Financial Report'}</h2>
            </div>
            <div className="space-y-4">
              <Input
                type="text"
                placeholder="Report Title"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
                className="border-slate-300 focus:border-blue-500 focus:ring-blue-500"
                required
              />
              <FileAttachmentUpload
                attachments={attachments}
                setAttachments={setAttachments}
              />
              <Textarea
                placeholder="Add comments here..."
                value={formData.content}
                onChange={(e) => setFormData(prev => ({...prev, content: e.target.value}))}
                className="border-slate-300 focus:border-blue-500 focus:ring-blue-500"
                rows={6}
                required
              />
            </div>
            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-slate-200">
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleFormReset}
                className="h-8 px-4 text-sm border-slate-300 text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                className="h-8 px-4 text-sm bg-blue-600 hover:bg-blue-700 text-white"
              >
                {editingId ? 'Update Report' : 'Post Report'}
              </Button>
            </div>
          </form>
        </Card>
      )}

      <div className="space-y-6">
        {reports.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <div className="text-slate-500 text-lg mb-2">No financial reports found</div>
            <div className="text-slate-400">Reports will appear here once they are published.</div>
          </div>
        ) : (
          reports.map((report) => (
            <Card key={report.id} className={`p-6 shadow-lg rounded-xl border border-slate-200 hover:shadow-xl transition-all duration-200 ${report.hidden ? 'bg-slate-100 opacity-70' : 'bg-white'}`}>
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold text-slate-900">{getDisplayTitle(report.title)}</h2>
                {isAdmin && user && report.id && (
                  <PostActions
                    isAdmin={isAdmin}
                    isSuperAdmin={isSuperAdmin}
                    hidden={!!report.hidden}
                    onEdit={() => startEdit(report)}
                    onDelete={() => handleDelete(report.id!)}
                    onHide={() => handleToggleVisibility(report.id!)}
                    onUnhide={() => handleToggleVisibility(report.id!)}
                  />
                )}
              </div>

              {report.attachments && report.attachments.length > 0 && (
                <div className="mb-6">
                  <AttachmentsDisplay attachments={report.attachments} />
                </div>
              )}
              
              <div className="prose max-w-none text-slate-700 mb-6">
                <p className="whitespace-pre-wrap leading-relaxed">{report.content}</p>
              </div>

              <div className="flex justify-between items-center text-sm text-slate-500 border-t border-slate-200 pt-4">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center">
                    <UserIcon className="w-4 h-4 mr-2 text-slate-400" />
                    <span className="font-medium">{users[report.authorId] || 'Unknown Author'}</span>
                  </div>
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-2 text-slate-400" />
                    <span>{formatDateTimeWithTimezone(report.created_at)}</span>
                  </div>
                </div>
                {user && report.authorId && (
                  <SendQuestionButton
                    authorId={report.authorId}
                    source="Financial Reports"
                  />
                )}
              </div>
            </Card>
          ))
        )}
      </div>
      </main>
    </div>
  );
};

export default FinancialReports; 